<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Customers</h1>
    <p class="text-muted mb-0">Manage your customer accounts</p>
  </div>
  <button class="btn btn-primary" (click)="addCustomer()">
    <i class="bi bi-plus-lg me-2"></i>Add Customer
  </button>
</div>

<!-- Stats Cards -->
<div class="row g-3 mb-4">
  <div class="col-md-4">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-primary-subtle text-primary">
            <i class="bi bi-people"></i>
          </div>
          <div>
            <div class="stat-value text-primary">{{ totalCustomers() }}</div>
            <div class="stat-label text-muted">Total Customers</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-success-subtle text-success">
            <i class="bi bi-geo-alt"></i>
          </div>
          <div>
            <div class="stat-value text-success">{{ totalFields() }}</div>
            <div class="stat-label text-muted">Total Fields</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card h-100">
      <div class="card-body p-3">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-info-subtle text-info">
            <i class="bi bi-rulers"></i>
          </div>
          <div>
            <div class="stat-value text-info">{{ totalAcreage() | number:'1.0-0' }}</div>
            <div class="stat-label text-muted">Total Acreage</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Search -->
<div class="card mb-4">
  <div class="card-body p-3">
    <div class="row g-3 align-items-end">
      <div class="col-md-10">
        <label class="form-label">Search</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-search"></i></span>
          <input type="text" class="form-control" placeholder="Search by name, city, or email..."
                 [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)">
        </div>
      </div>
      <div class="col-md-2">
        <button class="btn btn-outline-secondary w-100" (click)="clearFilters()"
                [disabled]="!searchQuery()">
          <i class="bi bi-x-lg me-1"></i>Clear
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Customers List -->
<div class="card">
  <div class="card-header d-flex justify-content-between align-items-center p-3">
    <h5 class="mb-0">Customers</h5>
    <span class="badge bg-secondary">{{ filteredCustomers().length }} customers</span>
  </div>
  <div class="card-body p-0">
    <ng-container *ngIf="filteredCustomers().length > 0; else emptyState">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>Location</th>
              <th>Fields</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let customer of filteredCustomers()">
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div class="customer-icon">
                    <i class="bi bi-person-fill"></i>
                  </div>
                  <div>
                    <div class="fw-medium">{{ customer.name }}</div>
                    <div class="small text-muted" *ngIf="customer.notes">{{ customer.notes | slice:0:50 }}{{ customer.notes.length > 50 ? '...' : '' }}</div>
                  </div>
                </div>
              </td>
              <td>
                <div *ngIf="customer.email" class="small">
                  <a [href]="'mailto:' + customer.email" class="text-decoration-none">{{ customer.email }}</a>
                </div>
                <div *ngIf="customer.phone" class="small text-muted">
                  <a [href]="'tel:' + customer.phone" class="text-decoration-none text-muted">{{ customer.phone | phoneFormat }}</a>
                </div>
                <span *ngIf="!customer.email && !customer.phone" class="text-muted">-</span>
              </td>
              <td>
                <div *ngIf="customer.city || customer.state">
                  {{ formatLocation(customer) }}
                </div>
                <span *ngIf="!customer.city && !customer.state" class="text-muted">-</span>
              </td>
              <td>
                <div *ngIf="getFieldCount(customer.id) > 0">
                  <span class="fw-medium">{{ getFieldCount(customer.id) }}</span>
                  <span class="text-muted"> field{{ getFieldCount(customer.id) !== 1 ? 's' : '' }}</span>
                  <div class="small text-muted">{{ getCustomerAcreage(customer.id) | number:'1.0-0' }} ac</div>
                </div>
                <span *ngIf="getFieldCount(customer.id) === 0" class="text-muted">No fields</span>
              </td>
              <td class="text-end">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" (click)="viewCustomer(customer)" title="View">
                    <i class="bi bi-eye"></i>
                  </button>
                  <button class="btn btn-outline-secondary" (click)="editCustomer(customer)" title="Edit">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <button class="btn btn-outline-secondary text-danger" (click)="deleteCustomer(customer)" title="Delete">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>

    <ng-template #emptyState>
      <div class="p-5 text-center">
        <div class="empty-state-icon mb-3">
          <i class="bi bi-people"></i>
        </div>
        <h5>No Customers Yet</h5>
        <p class="text-muted mb-3" *ngIf="!searchQuery()">
          Add your first customer to get started.
        </p>
        <p class="text-muted mb-3" *ngIf="searchQuery()">
          No customers match your search. Try adjusting your search criteria.
        </p>
        <button class="btn btn-primary" (click)="addCustomer()" *ngIf="!searchQuery()">
          <i class="bi bi-plus-lg me-2"></i>Add Customer
        </button>
        <button class="btn btn-outline-secondary" (click)="clearFilters()" *ngIf="searchQuery()">
          <i class="bi bi-x-lg me-2"></i>Clear Search
        </button>
      </div>
    </ng-template>
  </div>
</div>

<!-- Confirm Delete Modal -->
<app-confirm-modal
  #confirmModal
  (confirmed)="onDeleteConfirmed()"
  (cancelled)="onDeleteCancelled()"
></app-confirm-modal>
